import {
  addToaster,
  getGenericEntityListingAPI,
  hideLoader,
  previewAndDownload,
  SEND_MANUAL_COMMUNICATION_CONSTANT,
  showLoader,
  WorkflowInstanceTaskType,
} from '@app-store'
import { CustomButton, Modal, PdfViewer, SvgDownload, SvgView } from '@ui-kit'
import { FC, useEffect, useRef, useState } from 'react'
import {
  RootState,
  useAppDispatch,
  useAppSelector,
} from '../../utils/agent.store'
import { isEmpty, uniqueId } from 'lodash'
import { SystemAutoTaskPropsType } from '../../types/workflow.types'
import { NAMESPACES, TASK_STATUS, WORKFLOW_STATUS } from '../../utils/constants'
import {
  formatNumberTwoDigits,
  logErrorMessage,
  permissionObject,
} from '../../utils/helperFunctions'
import TaskNotesSuggestionBox from './TaskNotesSuggestion'
import { FieldValues, useForm } from 'react-hook-form'
import {
  editEntityDataEmbeddedAPI,
  getEntityDataByIdEmbedded,
} from '../../apis/entityData.api'
import { STRINGS } from '../../utils/strings'
import { createPortal } from 'react-dom'
import { currentUserId, isReadOnlyUser } from '../../utils/permission'

export const CaseCreatedTask: FC<SystemAutoTaskPropsType> = ({
  task,
  tabIndexOffset,
}) => {
  return (
    <div className="flex flex-col p-2">
      <span className="text-base font-primary font-normal">Case ID</span>
      <a
        className="text-blue-600 underline hover:text-blue-800"
        href={`${window.location.origin}/entity/${
          task?.identifier?.entity_id
        }/workflow/review/${Number(task?.resource_id)}`}
        target="_blank"
        rel="noopener noreferrer"
        tabIndex={tabIndexOffset}
      >
        {task?.notes}
      </a>
    </div>
  )
}

const DownloadPreviewSummary = ({
  task,
  productName,
}: {
  task: WorkflowInstanceTaskType
  productName?: string
}) => {
  const downloadDropdownRef = useRef<HTMLDivElement>(null)
  const previewDropdownRef = useRef<HTMLDivElement>(null)
  const [isDownloadOpen, setIsDownloadOpen] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isPreview, setIsPreview] = useState<string | null>(null)
  const dispatch = useAppDispatch()
  const downloadBtnRef = useRef<HTMLDivElement>(null)
  const previewBtnRef = useRef<HTMLDivElement>(null)
  const [downloadDropdownPosition, setDownloadDropdownPosition] = useState({
    top: 0,
    left: 0,
  })
  const [previewDropdownPosition, setPreviewDropdownPosition] = useState({
    top: 0,
    left: 0,
  })

  let documentObject: Record<string, number> = {}

  try {
    const docObjectRaw = task?.identifier?.document_object
    const safeString =
      typeof docObjectRaw === 'string' ? docObjectRaw.replace(/'/g, '"') : '{}'
    const stringValue = JSON.parse(safeString)
    documentObject =
      typeof stringValue === 'string' ? JSON.parse(stringValue) : stringValue
  } catch (error) {
    console.error('Invalid document_object format', error)
  }

  const previewFile = (url: string) => setIsPreview(url)

  const downloadFile = async (url: string, fileName: string) => {
    const response = await fetch(url)
    if (!response.ok) throw new Error(NAMESPACES.DOWNLOAD_ERROR)

    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const aTag = document.createElement('a')
    aTag.href = blobUrl
    aTag.download = fileName
    document.body.appendChild(aTag)
    aTag.click()
    document.body.removeChild(aTag)
  }

  const downloadOrPreviewDocx = async (
    docId: number,
    isPreviewMode = false,
    PolicyRank?: string
  ) => {
    try {
      dispatch(showLoader())
      const { data } = await previewAndDownload({
        entityId: Number(task?.case_id ?? ''),
        id: docId,
      })
      const url = data?.url
      const name = `${productName}_${PolicyRank}_Summary of Benefits`

      if (isPreviewMode) previewFile(url)
      else await downloadFile(url, name)

      dispatch(hideLoader())
    } catch (error) {
      dispatch(hideLoader())
      logErrorMessage('download and preview', error)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        downloadDropdownRef.current &&
        !downloadDropdownRef.current.contains(event.target as Node)
      ) {
        setIsDownloadOpen(false)
      }
      if (
        previewDropdownRef.current &&
        !previewDropdownRef.current.contains(event.target as Node)
      ) {
        setIsPreviewOpen(false)
      }
    }

    const handleScroll = () => {
      setIsDownloadOpen(false)
      setIsPreviewOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', handleScroll, true)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [])

  const toggleDownloadDropdown = () => {
    if (downloadBtnRef.current) {
      const rect = downloadBtnRef.current.getBoundingClientRect()
      setDownloadDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 90 + window.scrollX,
      })
    }
    setIsDownloadOpen((prev) => !prev)
  }

  const togglePreviewDropdown = () => {
    if (previewBtnRef.current) {
      const rect = previewBtnRef.current.getBoundingClientRect()
      setPreviewDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 90 + window.scrollX,
      })
    }
    setIsPreviewOpen((prev) => !prev)
  }

  return (
    <div className="flex items-center justify-end gap-5">
      {/* Download dropdown */}
      <div ref={downloadBtnRef} className="relative">
        <SvgDownload
          className="cursor-pointer"
          onClick={toggleDownloadDropdown}
        />
        {isDownloadOpen &&
          createPortal(
            <div
              ref={downloadDropdownRef}
              style={{
                position: 'absolute',
                top: `${downloadDropdownPosition.top}px`,
                left: `${downloadDropdownPosition.left}px`,
                zIndex: 1000,
              }}
              className="w-fit rounded-md bg-white border shadow-md"
            >
              <div className="flex flex-col">
                {Object.entries(documentObject).length > 0 ? (
                  Object.entries(documentObject).map(([key, id]) => (
                    <button
                      key={key}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        downloadOrPreviewDocx(
                          id,
                          false,
                          e?.currentTarget?.textContent ?? ''
                        )
                        setIsDownloadOpen(false)
                      }}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-xs"
                    >
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-xs">
                    {STRINGS.NO_DATA_AVAILABLE}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Preview dropdown */}
      <div ref={previewBtnRef} className="relative">
        <SvgView className="cursor-pointer" onClick={togglePreviewDropdown} />
        {isPreviewOpen &&
          createPortal(
            <div
              ref={previewDropdownRef}
              style={{
                position: 'absolute',
                top: `${previewDropdownPosition.top}px`,
                left: `${previewDropdownPosition.left}px`,
                zIndex: 1000,
              }}
              className="w-fit rounded-md bg-white border shadow-md"
            >
              <div className="flex flex-col">
                {Object.entries(documentObject).length > 0 ? (
                  Object.entries(documentObject).map(([key, id]) => (
                    <button
                      key={key}
                      onClick={() => {
                        downloadOrPreviewDocx(id, true)
                        setIsPreviewOpen(false)
                      }}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-xs"
                    >
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-gray-500 text-xs">
                    {STRINGS.NO_DATA_AVAILABLE}
                  </div>
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Modal */}
      {!isEmpty(isPreview) && (
        <Modal
          isOpen={!!isPreview}
          closeModal={() => setIsPreview(null)}
          showCloseButton={true}
        >
          <div className="flex editer-wrap justify-center w-[50rem]">
            <div className="w-fit min-h-[21cm] with-shadow ck-content document-editor__editable-container bg-accent-0 p-4">
              <PdfViewer
                pdfUrl={isPreview ?? ''}
                allowZoom={false}
                showPageNumber={false}
                defaultZoomScale={1.3}
                showLoader={true}
              />
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

const SystemAutoTask: FC<SystemAutoTaskPropsType> = ({
  task,
  reFetch,
  tabIndexOffset,
  isSummary = false,
  isDisabled = false,
  recordId,
}) => {
  const dispatch = useAppDispatch()
  const [notesState, setNotesState] = useState<boolean>(false)
  const [taskNoteBoxId, setTaskNoteBoxId] = useState<string | undefined>()
  const [productName, setProductName] = useState<string>()
  const [taskNotesData, setTaskNotesData] = useState<FieldValues>()
  const { setValue, register, getValues, watch } = useForm()
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const programPermissions = useAppSelector(
    (state: RootState) => state.clientPermissions?.programPermissions
  )
  const caseData = useAppSelector(
    (state: RootState) => state.workflowData?.caseData
  )
  const userId = useAppSelector((state: RootState) => state?.authUser?.user?.id)
  const fetchTaskNotesData = async () => {
    try {
      dispatch(showLoader())
      const payload = {
        entity_name:
          workflowDefinition?.global_variables?.entities?.task?.entity_name ??
          NAMESPACES.TASK,
        select: ['c_case_notes_task', NAMESPACES.NAME_KEY],
        filters: [{ criteria: [] }],
        related_entities: {
          c_case_notes_task: {
            select: [NAMESPACES.NAME_KEY],
            criteria: [{ criteria: [] }],
          },
        },
      }
      const responseData = await getGenericEntityListingAPI(payload, task?.id)
      setTaskNotesData(responseData?.c_case_notes_task?.[0]?.name)
      if (responseData?.c_case_notes_task?.[0]?.id)
        setTaskNoteBoxId(
          responseData?.c_case_notes_task?.[0]?.id ?? task?.notes
        )
      setValue(
        `taskNotes${task?.task_id}`,
        responseData?.c_case_notes_task?.[0]?.name ?? task?.notes
      )
    } catch (error) {
      dispatch(
        addToaster({
          message: NAMESPACES.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
      logErrorMessage('taskNotes', error)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchProductName = async () => {
    const response = await getEntityDataByIdEmbedded(
      workflowDefinition?.global_variables?.entity_name ?? '',
      Number(workflowDefinition?.global_variables?.entity_id),
      [SEND_MANUAL_COMMUNICATION_CONSTANT.PRODUCT_ENTITY_NAME],
      Number(recordId)
    )
    if (response?.data)
      setProductName(response?.data?.products?.trademarked_name ?? '')
  }

  useEffect(() => {
    if (workflowDefinition && task) {
      fetchTaskNotesData()
      fetchProductName()
    }
  }, [workflowDefinition, task])

  const updateTask = async () => {
    try {
      dispatch(showLoader())
      await editEntityDataEmbeddedAPI(
        workflowDefinition?.global_variables?.entities?.task?.entity_name ??
          NAMESPACES.TASK,
        task?.id ?? '',
        {
          c_case_notes_task: [
            {
              name: getValues(`taskNotes${task?.task_id}`),
              source: NAMESPACES.TASK,
              headers: `T${formatNumberTwoDigits(
                task?.task_id ? +task?.task_id : 0
              )}- ${task?.name}`,
              ...(taskNoteBoxId ? { id: taskNoteBoxId } : {}),
              _state: NAMESPACES.CHANGED,
              case: {
                id: task?.case_id,
                _state: NAMESPACES.CHANGED,
              },
              ...(!taskNoteBoxId ? { users_id: currentUserId() ?? '' } : {}),
            },
          ],
          record_status: TASK_STATUS.ACTIVE,
        },
        ['c_case_notes_task']
      )
      dispatch(
        addToaster({
          message: NAMESPACES.TASK_NOTES_SAVED,
          type: 'success',
          id: uniqueId(),
        })
      )
      reFetch?.()
    } catch (error) {
      dispatch(
        addToaster({
          message: NAMESPACES.SOMETHING_WENT_WRONG,
          type: 'error',
          id: uniqueId(),
        })
      )
      logErrorMessage('taskNotes', error)
    } finally {
      setNotesState(false)
      dispatch(hideLoader())
    }
  }

  const isSaveVisible = () => {
    return permissionObject.Manage_Workflow(
      programPermissions,
      caseData,
      task,
      userId ?? ''
    )
  }

  return (
    <div className="p-2">
      {isSummary ? (
        <DownloadPreviewSummary task={task} productName={productName} />
      ) : null}
      {task?.identifier?.taskNoteValues?.isTaskNoteSuggestionBox ? (
        <div>
          <div className="flex flex-row-reverse pb-2">
            {isSaveVisible() ? (
              !notesState && taskNotesData ? (
                <div className="flex justify-between items-center">
                  <CustomButton
                    isFilled
                    disabled={
                      isReadOnlyUser() ||
                      task?.state !== WORKFLOW_STATUS.OPEN ||
                      isDisabled
                    }
                    color="primary"
                    onClick={() => setNotesState(true)}
                    tabIndex={tabIndexOffset}
                  >
                    {STRINGS.EDIT}
                  </CustomButton>
                </div>
              ) : (
                <CustomButton
                  isFilled
                  color="primary"
                  onClick={updateTask}
                  tabIndex={tabIndexOffset}
                  disabled={
                    isReadOnlyUser() ||
                    !watch(`taskNotes${task?.task_id}`)?.trim() ||
                    task?.state !== WORKFLOW_STATUS.OPEN ||
                    isDisabled
                  }
                >
                  {STRINGS.SAVE}
                </CustomButton>
              )
            ) : null}
          </div>
          <TaskNotesSuggestionBox
            task={task}
            mode={notesState || !taskNotesData ? 'edit' : 'view'}
            setValue={setValue}
            register={register}
            isDisabled={
              isReadOnlyUser() ||
              task?.state !== WORKFLOW_STATUS.OPEN ||
              isDisabled
            }
            isShowSuggestion={
              task?.identifier?.taskNoteValues?.isTaskNoteAddButton
            }
          />
        </div>
      ) : null}
    </div>
  )
}

export default SystemAutoTask
